import * as React from "react";
import { History, MousePointer2, Sparkles, Trash2, Undo2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  apiErrorMessage,
  createPoll,
  draftBookingDescription,
  getEntitlements,
  listIntegrations,
  updatePoll,
} from "@/api/client";
import { BookingWindow, Poll, PollUpsertRequest } from "@/api/types";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { VideoOption, videoOptions } from "@/lib/calendarProviders";
import { cn } from "@/lib/utils";

import { markBuilderReturn } from "../builderReturn";
import { clearPollDraft, loadPollDraft, pollDraftHasContent, savePollDraft } from "../draft";
import { DurationField } from "../DurationField";
import { MeetingLocationPicker } from "../MeetingLocationPicker";
import { BuilderSocialNote } from "../BuilderSocialNote";
import { ToolbarPopover } from "../ToolbarPopover";
import { PollAvailabilityCalendar, restampWindows } from "./PollAvailabilityCalendar";

/**
 * The Poll host builder. Shares the other two builders' product discipline — one canvas-tinted
 * settings column of thin-ruled panels, the AI rich-text description beside it, the shared
 * meeting-location picker, the duration control living on the calendar toolbar — with the fields a
 * poll actually has: a deadline, a counts toggle, and a set of proposed times.
 *
 * <p>Two things are deliberately absent. There is no fill-from-a-availability-pattern: a poll asks a
 * specific group about a handful of specific times, and generating a week of them from a pattern is
 * the opposite of that. And the duration control is labelled Duration, not Cursor — on a poll it
 * really is the length of the event being decided, not just the size of the drawing brush.</p>
 */

const DOTTED_CANVAS: React.CSSProperties = {
  backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1.3px)",
  backgroundSize: "14px 14px",
};

const MIN_DURATION = 5;
const MAX_DURATION = 480;

/** A settings section. `grouped` sits inside the shared canvas container, separated by thin rules. */
function Panel({
  title,
  action,
  grouped,
  className,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  grouped?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <section
      className={cn(grouped ? "p-3" : "rounded-xl bg-muted/40 p-4", className)}
      style={grouped ? undefined : DOTTED_CANVAS}
    >
      {(title || action) && (
        <div className="mb-2 flex min-h-[1.5rem] items-center justify-between gap-2">
          {title && <h3 className="text-xs font-bold uppercase tracking-wide text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      {children && <div>{children}</div>}
    </section>
  );
}

/** The "tokens used" pill beside the AI button. */
function TokenBadge({ value }: { value: number }) {
  return (
    <span
      title="Tokens the last AI draft used"
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-contrast/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-brand-contrast"
    >
      <Sparkles className="h-3 w-3" />~{value.toLocaleString()} tokens
    </span>
  );
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The poll's proposed times as calendar windows. */
function windowsOf(poll: Poll | null): BookingWindow[] {
  if (!poll) return [];
  return poll.options.map((o) => ({ start: o.start_at, end: o.end_at }));
}

export function PollBuilder({
  existing,
  onSaved,
  onCancel,
  onError,
}: {
  /** An open poll being edited, or null for a new one. */
  existing: Poll | null;
  onSaved: (poll: Poll) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  // Per-link branding. Hydrated from the link being edited, so opening an existing one does not
  // quietly switch a host's choice back on; a new link starts with both on.
  const [showHostAvatar, setShowHostAvatar] = React.useState(existing?.show_host_avatar ?? true);
  const [showHostBanner, setShowHostBanner] = React.useState(existing?.show_host_banner ?? true);
  const navigate = useNavigate();
  const timezone = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    [],
  );

  // A restored draft only applies to a NEW poll; an existing one is the source of truth.
  const draft = React.useMemo(() => (existing ? null : loadPollDraft()), [existing]);
  const [restored, setRestored] = React.useState(pollDraftHasContent(draft));
  const [fading, setFading] = React.useState(false);
  React.useEffect(() => {
    if (!restored) return;
    const t = window.setTimeout(() => setFading(true), 6000);
    return () => window.clearTimeout(t);
  }, [restored]);

  const [title, setTitle] = React.useState(existing?.title ?? draft?.title ?? "");
  const [description, setDescription] = React.useState(
    existing?.description ?? draft?.description ?? "",
  );
  const [durationMinutes, setDurationMinutes] = React.useState(
    existing?.duration_minutes ?? draft?.durationMinutes ?? 30,
  );
  const [provider, setProvider] = React.useState(
    existing?.conferencing_provider ?? draft?.provider ?? "",
  );
  const [physicalOn, setPhysicalOn] = React.useState(
    existing ? !!existing.location : (draft?.physicalOn ?? false),
  );
  const [location, setLocation] = React.useState(existing?.location ?? draft?.location ?? "");
  const [respondBy, setRespondBy] = React.useState(existing?.respond_by ?? draft?.respondBy ?? "");
  const [showCounts, setShowCounts] = React.useState(
    existing?.show_counts ?? draft?.showCounts ?? false,
  );
  const [windows, setWindows] = React.useState<BookingWindow[]>(
    existing ? windowsOf(existing) : (draft?.windows ?? []),
  );
  const [showMine, setShowMine] = React.useState(draft?.showMine ?? false);
  const [saving, setSaving] = React.useState(false);

  // Changing the duration rewrites every time the host has already placed, so it is undoable.
  const [undoWindows, setUndoWindows] = React.useState<BookingWindow[] | null>(null);
  const [undoDuration, setUndoDuration] = React.useState<number | null>(null);

  // Null until they load, so the picker says so rather than showing a list that is about to
  // change under the host. Same shape as the ad-hoc and rolling builders.
  const [videoChoices, setVideoChoices] = React.useState<VideoOption[] | null>(null);
  React.useEffect(() => {
    listIntegrations()
      .then((v) => setVideoChoices(videoOptions(v)))
      .catch(() => {
        setVideoChoices(videoOptions(null));
      });
  }, []);

  const [tier, setTier] = React.useState<string>("");
  React.useEffect(() => {
    getEntitlements()
      .then((e) => setTier(e.tier))
      .catch(() => {
        /* non-fatal — the server enforces every gate itself */
      });
  }, []);
  const isPro = tier === "PRO" || tier === "ENTERPRISE";

  const [aiDrafting, setAiDrafting] = React.useState(false);
  const [lastDraftTokens, setLastDraftTokens] = React.useState<number | null>(null);

  // Autosave the in-progress NEW poll on every change (session-scoped), so leaving and returning —
  // or a reload — comes back to exactly this state. An existing poll is never drafted.
  React.useEffect(() => {
    if (existing) return;
    savePollDraft({
      title,
      description,
      durationMinutes,
      provider,
      physicalOn,
      location,
      respondBy,
      showCounts,
      windows,
      showMine,
    });
  }, [
    existing,
    title,
    description,
    durationMinutes,
    provider,
    physicalOn,
    location,
    respondBy,
    showCounts,
    windows,
    showMine,
  ]);

  const discardDraft = () => {
    clearPollDraft();
    setRestored(false);
    setTitle("");
    setDescription("");
    setDurationMinutes(30);
    setProvider("");
    setPhysicalOn(false);
    setLocation("");
    setRespondBy("");
    setShowCounts(false);
    setWindows([]);
  };

  const changeDuration = (next: number) => {
    if (next === durationMinutes) return;
    if (windows.length > 0) {
      setUndoWindows(windows);
      setUndoDuration(durationMinutes);
      setWindows(restampWindows(windows, next));
    }
    setDurationMinutes(next);
  };

  const undoRestamp = () => {
    if (undoWindows) setWindows(undoWindows);
    if (undoDuration != null) setDurationMinutes(undoDuration);
    setUndoWindows(null);
    setUndoDuration(null);
  };

  const starts = React.useMemo(
    () => [...windows].sort((a, b) => Date.parse(a.start) - Date.parse(b.start)).map((w) => w.start),
    [windows],
  );

  // The deadline cannot outlive the first thing it is deciding, and the calendar bounds enforce it
  // rather than an error appearing after the fact.
  const firstStartDate = starts.length > 0 ? ymd(new Date(starts[0])) : "";
  const today = ymd(new Date());

  const createHint = (() => {
    if (!title.trim()) return "Give this poll a title.";
    if (windows.length < 2) return "Propose at least two times for people to choose between.";
    if (respondBy && respondBy < today) return "The respond-by date is in the past.";
    if (respondBy && firstStartDate && respondBy > firstStartDate) {
      return "The respond-by date is after the first proposed time.";
    }
    return null;
  })();
  const canSave = !createHint && !saving;

  const draftWithAi = async (): Promise<void> => {
    if (!isPro) {
      navigate("/upgrade");
      return;
    }
    if (title.trim() === "") {
      onError("Give the poll a title first. The AI drafts the description from it.");
      return;
    }
    setAiDrafting(true);
    try {
      const drafted = await draftBookingDescription({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setDescription(drafted.markdown);
      setLastDraftTokens((drafted.input_tokens ?? 0) + (drafted.output_tokens ?? 0));
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't draft a description just now"));
    } finally {
      setAiDrafting(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      const req: PollUpsertRequest = {
        title: title.trim(),
        description: description.trim() || null,
        location: physicalOn && location.trim() ? location.trim() : null,
        conferencing_provider: provider || null,
        duration_minutes: durationMinutes,
        timezone,
        respond_by: respondBy || null,
        show_counts: showCounts,
        show_host_avatar: showHostAvatar,
        show_host_banner: showHostBanner,
        starts,
      };
      const saved = existing ? await updatePoll(existing.id, req) : await createPoll(req);
      if (!existing) clearPollDraft();
      onSaved(saved);
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't save this poll"));
    } finally {
      setSaving(false);
    }
  };

  const durationControl = (
    <ToolbarPopover
      label={
        <span className="inline-flex items-center gap-1">
          <MousePointer2 className="h-3.5 w-3.5 text-brand-blue" />
          Duration {Math.floor(durationMinutes / 60)}:{String(durationMinutes % 60).padStart(2, "0")}
        </span>
      }
      title="How long the meeting will be"
      ariaLabel="Duration"
      align="center"
      active
      menuClassName="w-72"
    >
      <DurationField
        minutes={durationMinutes}
        onChange={changeDuration}
        min={MIN_DURATION}
        max={MAX_DURATION}
      />
    </ToolbarPopover>
  );

  const clearControl =
    windows.length > 0 ? (
      <button
        type="button"
        onClick={() => setWindows([])}
        title="Remove every proposed time"
        className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:border-brand-coral hover:bg-brand-coral/10 hover:text-brand-coral"
      >
        <Trash2 className="h-3.5 w-3.5" /> Clear
      </button>
    ) : null;

  return (
    <div className="comic-shadow relative space-y-5 rounded-xl border border-border bg-card p-5">
      {restored && (
        <div
          onTransitionEnd={(e) => {
            if (fading && e.propertyName === "opacity") setRestored(false);
          }}
          className={cn(
            "absolute right-4 top-4 z-20 flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-brand-blue/40 bg-brand-blue/10 px-3 py-1.5 text-xs shadow-sm backdrop-blur transition-opacity duration-[2500ms] ease-in",
            fading ? "opacity-0" : "opacity-100",
          )}
        >
          <span className="inline-flex items-center gap-1.5 font-medium text-brand-blue">
            <History className="h-3.5 w-3.5 shrink-0" />
            Draft restored. We kept what you were working on.
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={discardDraft}
              className="rounded-full px-2 py-0.5 font-semibold text-brand-blue underline-offset-2 hover:underline"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => setRestored(false)}
              title="Dismiss"
              aria-label="Dismiss"
              className="grid h-6 w-6 place-items-center rounded-full text-brand-blue hover:bg-brand-blue/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {/* Title and location on the left, description beside it — the field order the poll asks for,
          in the two-column shape the other two builders already use. */}
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr] lg:items-start">
        <div className="min-w-0 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="poll-title" className="text-xs font-bold uppercase tracking-wide text-foreground">
              Title
            </Label>
            <Input
              id="poll-title"
              value={title}
              maxLength={120}
              placeholder="e.g. Team retro"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="divide-y divide-border rounded-xl bg-muted/40" style={DOTTED_CANVAS}>
            <Panel
              grouped
              title="Meeting location"
              action={
                <div className="min-w-0 max-w-[15rem] flex-1">
                  <MeetingLocationPicker
                    provider={provider}
                    onProviderChange={setProvider}
                    physicalOn={physicalOn}
                    onPhysicalChange={setPhysicalOn}
                    location={location}
                    onLocationChange={setLocation}
                    videoOptions={videoChoices}
                    onConnect={() => {
                      // The half-built poll is already autosaved; this is what remembers WHICH
                      // builder held it, so they come back here and not to the ad-hoc one.
                      markBuilderReturn("poll");
                      navigate("/profile#calendar-integrations");
                    }}
                  />
                </div>
              }
            >
              {provider && (
                <p className="text-sm leading-snug text-brand-blue">
                  A link is created when you confirm the time, and added to the invitation.
                </p>
              )}
            </Panel>

            <BuilderSocialNote
              offering="poll"
              showAvatar={showHostAvatar}
              showBanner={showHostBanner}
              onShowAvatarChange={setShowHostAvatar}
              onShowBannerChange={setShowHostBanner}
            />

            <Panel
              grouped
              title="Respond by"
              action={
                <input
                  type="date"
                  value={respondBy}
                  min={today}
                  max={firstStartDate || undefined}
                  onChange={(e) => setRespondBy(e.target.value)}
                  aria-label="Respond by"
                  className="rounded-md border border-foreground bg-background px-2.5 py-1 text-sm tabular-nums text-foreground"
                />
              }
            />

            <Panel
              grouped
              title="Show vote counts to invitees"
              action={
                <Switch
                  checked={showCounts}
                  onCheckedChange={setShowCounts}
                  aria-label="Show vote counts to invitees"
                />
              }
            />

          </div>
        </div>

        <div className="min-w-0">
          <Panel
            title="Description (optional)"
            action={
              <div className="flex items-center gap-2">
                {lastDraftTokens != null && <TokenBadge value={lastDraftTokens} />}
                <button
                  type="button"
                  onClick={draftWithAi}
                  disabled={aiDrafting}
                  title={
                    isPro
                      ? "Let AI draft from your title, or improve the text you've written"
                      : "Drafting is a Pro feature. See plans"
                  }
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-brand-contrast/30 bg-brand-contrast/10 px-2.5 py-1 text-xs font-semibold text-brand-contrast transition hover:bg-brand-contrast/15 disabled:opacity-60"
                >
                  <Sparkles className="h-3 w-3 shrink-0" />
                  {aiDrafting
                    ? "Drafting…"
                    : isPro
                      ? "Redraft with AI from title + description"
                      : "Redraft with AI from title + description (Pro)"}
                </button>
              </div>
            }
          >
            {/* `fill` starts the editor tall (~26rem) so it stands level with the settings column
                beside it. The ad-hoc builder has always passed it; this one did not, which is what
                left a band of empty card below the box. */}
            <MarkdownEditor
              id="poll-desc"
              value={description}
              onChange={setDescription}
              maxLength={300}
              placeholder="What's this poll for? Use the toolbar, or let AI draft a first version."
              fill
            />
          </Panel>
        </div>
      </div>

      <PollAvailabilityCalendar
        windows={windows}
        onChange={setWindows}
        durationMinutes={durationMinutes}
        showMine={showMine}
        headerLeft={clearControl}
        headerCenter={durationControl}
        headerAbove={
          <label className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={showMine}
              onChange={(e) => setShowMine(e.target.checked)}
            />
            Show my confirmed meetings
          </label>
        }
        topNotice={
          undoWindows ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-blue/40 bg-brand-blue/10 px-3 py-1.5 text-xs font-medium text-brand-blue shadow-sm backdrop-blur">
              Every proposed time is now {durationMinutes} minutes.
              <button
                type="button"
                onClick={undoRestamp}
                className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline"
              >
                <Undo2 className="h-3.5 w-3.5" /> Undo
              </button>
            </span>
          ) : null
        }
      />

      <p className="text-xs text-muted-foreground">
        All times in <span className="font-medium text-foreground">{timezone}</span>.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={!canSave} title={createHint ?? undefined}>
          {saving ? "Saving…" : existing ? "Save changes" : "Create poll"}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {createHint && (
          <span className="text-sm font-medium text-amber-700 dark:text-amber-500">{createHint}</span>
        )}
      </div>
    </div>
  );
}
